import { useParams, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { useMessageDetail, useUpdateMessageReadStatus } from '../../hooks/queries/messageQueries';
import { useEffect } from 'react';
import dayjs from 'dayjs';
import ChevronLeftIcon from '../../assets/icons/chevron-left.svg?react';
import ReadIcon from '../../assets/icons/read.svg?react';
import UnreadIcon from '../../assets/icons/unread.svg?react';
import './SystemAlertDetail.css';

const SystemAlertDetail = () => {
  const { messageSysId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  
  const { data: message, isLoading, error } = useMessageDetail(user?.UserSysId, messageSysId);
  const updateReadStatus = useUpdateMessageReadStatus(user?.UserSysId, messageSysId);

  // 메시지 상세 조회 시 자동으로 읽음 처리
  useEffect(() => {
    if (message && !message.IsRead && user?.UserSysId && messageSysId) {
      updateReadStatus.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message, user?.UserSysId, messageSysId]);

  const handleBack = () => {
    navigate('/system-alert-history');
  };

  if (isLoading) {
    return (
      <div className="system-alert-detail-container">
        <div className="system-alert-detail-loading">
          <div className="loading-spinner"></div>
          <p>알림 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="system-alert-detail-container">
        <div className="system-alert-detail-error">
          <p className="text-b1-400 text-gray-700">알림 정보를 불러오는데 실패했습니다.</p>
          <button onClick={handleBack} className="btn big-btn btn-primary">
            목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  if (!message) {
    return (
      <div className="system-alert-detail-container">
        <div className="system-alert-detail-not-found">
          <h2 className="text-b2-400 text-gray-800">알림을 찾을 수 없습니다</h2>
          <p className="text-b2-400 text-gray-800">요청하신 알림이 존재하지 않거나 삭제되었습니다.</p>
          <button onClick={handleBack} className="btn big-btn btn-primary">
            <span className="text-b2-400 text-gray-800">목록으로 돌아가기</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="system-alert-detail-container">
      <div className="system-alert-detail-content">
        {/* 헤더 */}
        <div className="system-alert-detail-header">
          <button className="btn icon-btn" onClick={handleBack} title="목록으로 돌아가기">
            <ChevronLeftIcon color="var(--gray-600)" />
          </button>
          <h2 className="text-b1-700 text-gray-800">시스템 알림 상세</h2>
        </div>

        {/* 메시지 정보 */}
        <div className="system-alert-detail-info">
          <div className="system-alert-detail-section">
            <h3 className="text-b2-700 text-gray-700">기본 정보</h3>
            <table className="gray-table">
              <tbody>
                <tr>
                  <th width="180px">읽음 상태</th>
                  <td>
                    <div className="flex align-center gap-5">
                      {message.IsRead ? (
                        <>
                          <ReadIcon />
                          <span>읽음</span>
                        </>
                      ) : (
                        <>
                          <UnreadIcon />
                          <span>읽지 않음</span>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <th width="180px">제목</th>
                  <td>{message.Title || '-'}</td>
                </tr>
                <tr>
                  <th width="180px">유형</th>
                  <td>{message.Type || '-'}</td>
                </tr>
                <tr>
                  <th width="180px">발생일시</th>
                  <td>
                    {message.IssuedDate 
                      ? dayjs(message.IssuedDate).format('YYYY-MM-DD HH:mm:ss')
                      : '-'
                    }
                  </td>
                </tr>
                {message.ExpiredDate && (
                  <tr>
                    <th width="180px">만료일시</th>
                    <td>
                      {dayjs(message.ExpiredDate).format('YYYY-MM-DD HH:mm:ss')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* 메시지 내용 */}
          {message.Content && (
            <div className="system-alert-detail-section">
              <h3 className="text-b2-700 text-gray-700">내용</h3>
              <div className="system-alert-detail-content-text text-b2-400 text-gray-700">
                {message.Content}
              </div>
            </div>
          )}

          {/* 추가 정보 */}
          {message.Description && (
            <div className="system-alert-detail-section">
              <h3 className="text-b2-700 text-gray-700">설명</h3>
              <div className="system-alert-detail-content-text text-b2-400 text-gray-700">
                {message.Description}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemAlertDetail;

